import { defineComponent as _defineComponent } from 'vue';
import { withKeys as _withKeys, createElementVNode as _createElementVNode, resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, openBlock as _openBlock, createElementBlock as _createElementBlock } from "vue";
const _hoisted_1 = { class: "flex items-center gap-4" };
const _hoisted_2 = { class: "flex items-center gap-2" };
const _hoisted_3 = { class: "text-surface-600 text-sm w-[200px] text-center" };
import { computed, ref, watch } from "vue";
import { INTERVAL, CUSTOM_DATE_RANGES_LABELS, DATE_PICKER_LOCALES, } from "@/utils/const";
import dayjs from "dayjs";
import { DEFAULT_DATE_RANGE, generateDailyRangeDates, findFromAndToFromDateRange, TODAY_DATE_RANGE, } from "@/utils/util";
import { $tf } from "@/plugins/i18n";
import { useI18n } from "vue-i18n";
import DatePickerPopover from "@/components/datepicker/DatePickerPopover.vue";
export default /*@__PURE__*/ _defineComponent({
    __name: 'CustomeDatePicker',
    props: {
        selectedInterval: { default: INTERVAL.DAILY },
        initialValue: { default: () => ({ from: null, to: null }) }
    },
    emits: ["update:fromAndTo"],
    setup(__props, { emit: __emit }) {
        const props = __props;
        const emit = __emit;
        // region Date picker value
        const datePickerValue = ref(null);
        const datePickerCustomLabel = ref("");
        const updateDatePickerValue = (value) => {
            datePickerValue.value = value;
        };
        const updateDatePickerCustomLabel = (label) => {
            datePickerCustomLabel.value = label;
        };
        // endregion
        // region Language specific label
        const { locale } = useI18n();
        const getMonthName = (month) => {
            return DATE_PICKER_LOCALES[locale.value].monthNamesShort[month];
        };
        const getDateRangeString = (fromDate, toDate) => {
            if (!fromDate || !toDate) {
                return "";
            }
            const fromDay = fromDate.getDate();
            const fromMonth = getMonthName(fromDate.getMonth());
            const fromYear = fromDate.getFullYear();
            const toDay = toDate.getDate();
            const toMonth = getMonthName(toDate.getMonth());
            const toYear = toDate.getFullYear();
            const sameYear = fromYear === toYear;
            const sameMonth = fromMonth === toMonth;
            if (sameYear && sameMonth) {
                // If range is same year and month => 18 - 21 Dec 2025
                return `${fromDay} - ${toDay} ${fromMonth} ${fromYear}`;
            }
            else if (sameYear && !sameMonth) {
                // If range is same year and different month => 18 Nov - 21 Dec 2025
                return `${fromDay} ${fromMonth} - ${toDay} ${toMonth} ${fromYear}`;
            }
            else {
                // If range is different year => 18 Dec 2025 - 21 Jan 2026
                return `${fromDay} ${fromMonth} ${fromYear} - ${toDay} ${toMonth} ${toYear}`;
            }
        };
        const dailyDateRangeString = computed(() => {
            if (!datePickerValue.value?.[0] || !datePickerValue.value?.[1]) {
                return "";
            }
            return getDateRangeString(datePickerValue.value[0], datePickerValue.value[1]);
        });
        const weeklyDateRangeString = computed(() => {
            // @ts-ignore
            const dates = datePickerValue.value?.map((date) => dayjs(date)) ?? [];
            const fromDate = dayjs.min(dates);
            const toDate = dayjs.max(dates);
            // TODO: What if the user selects W11-13 and W15?
            // TODO: What if the user selects W11-13 and W15?
            // TODO: What if the user selects W11-13 and W15?
            // TODO: What if the user selects W11-13 and W15?
            return getDateRangeString(fromDate.toDate(), toDate.toDate());
        });
        const displayLabel = computed(() => {
            // If a custom label is selected, return the translated label except for the default label
            if (datePickerCustomLabel.value &&
                datePickerCustomLabel.value !== CUSTOM_DATE_RANGES_LABELS.DEFAULT) {
                return $tf(datePickerCustomLabel.value);
            }
            else {
                // If no custom label is selected, return the formatted date range
                return props.selectedInterval === INTERVAL.WEEKLY
                    ? weeklyDateRangeString.value
                    : dailyDateRangeString.value;
            }
        });
        // endregion
        // region Initiate date range
        const initiateDateRange = () => {
            if (props.selectedInterval === INTERVAL.WEEKLY) {
                const startDate = DEFAULT_DATE_RANGE[props.selectedInterval].from;
                const endDate = DEFAULT_DATE_RANGE[props.selectedInterval].to;
                return [...generateDailyRangeDates(startDate, endDate)];
            }
            else if (props.selectedInterval === INTERVAL.DAILY) {
                return [
                    DEFAULT_DATE_RANGE[props.selectedInterval].from,
                    DEFAULT_DATE_RANGE[props.selectedInterval].to,
                ];
            }
        };
        const isFirstRender = ref(true);
        watch(() => props.selectedInterval, () => {
            // If the initial value is set and it is the first render, set the date picker value to the initial value
            if (props.initialValue.from &&
                props.initialValue.to &&
                isFirstRender.value) {
                if (props.selectedInterval === INTERVAL.WEEKLY) {
                    datePickerValue.value = generateDailyRangeDates(dayjs(props.initialValue.from), dayjs(props.initialValue.to));
                }
                else if (props.selectedInterval === INTERVAL.DAILY) {
                    datePickerValue.value = [
                        dayjs(props.initialValue.from).toDate(),
                        dayjs(props.initialValue.to).toDate(),
                    ];
                }
                isFirstRender.value = false;
            }
            else {
                datePickerValue.value = initiateDateRange();
                datePickerCustomLabel.value = CUSTOM_DATE_RANGES_LABELS.DEFAULT;
                isFirstRender.value = false;
            }
        }, { immediate: true });
        watch(datePickerValue, (value) => {
            const { from, to } = findFromAndToFromDateRange(value);
            if (from && to) {
                emit("update:fromAndTo", { from, to });
            }
        }, { immediate: true });
        // endregion
        // region Popover ref
        const datePickerRef = ref(null);
        const openDatePickerPopover = ($event) => {
            datePickerRef.value?.datePickerPopoverRef?.toggle($event);
        };
        // endregion
        // region Control logic
        const jumpToToday = () => {
            if (props.selectedInterval === INTERVAL.WEEKLY) {
                datePickerValue.value = generateDailyRangeDates(TODAY_DATE_RANGE[props.selectedInterval].from, TODAY_DATE_RANGE[props.selectedInterval].to);
            }
            else if (props.selectedInterval === INTERVAL.DAILY) {
                datePickerValue.value = [
                    TODAY_DATE_RANGE[props.selectedInterval].from,
                    TODAY_DATE_RANGE[props.selectedInterval].to,
                ];
            }
        };
        const onPrevious = () => {
            if (props.selectedInterval === INTERVAL.WEEKLY) {
                const { from, to } = findFromAndToFromDateRange(datePickerValue.value);
                const newFrom = dayjs(from).startOf("week").subtract(1, "week").toDate();
                const newTo = dayjs(to).endOf("week").subtract(1, "week").toDate();
                datePickerValue.value = generateDailyRangeDates(newFrom, newTo);
            }
            else if (props.selectedInterval === INTERVAL.DAILY) {
                const from = dayjs(datePickerValue.value[0]).subtract(1, "day").toDate();
                const to = dayjs(datePickerValue.value[1]).subtract(1, "day").toDate();
                datePickerValue.value = [from, to];
            }
            datePickerCustomLabel.value = "";
        };
        const onNext = () => {
            if (props.selectedInterval === INTERVAL.WEEKLY) {
                const { from, to } = findFromAndToFromDateRange(datePickerValue.value);
                const newFrom = dayjs(from).startOf("week").add(1, "week").toDate();
                const newTo = dayjs(to).endOf("week").add(1, "week").toDate();
                datePickerValue.value = generateDailyRangeDates(newFrom, newTo);
            }
            else if (props.selectedInterval === INTERVAL.DAILY) {
                datePickerValue.value = [
                    dayjs(datePickerValue.value[0]).add(1, "day").toDate(),
                    dayjs(datePickerValue.value[1]).add(1, "day").toDate(),
                ];
            }
            datePickerCustomLabel.value = "";
        };
        // endregion
        return (_ctx, _cache) => {
            const _component_DatePickerControlButton = _resolveComponent("DatePickerControlButton");
            return (_openBlock(), _createElementBlock("div", _hoisted_1, [
                _createElementVNode("i", {
                    class: "fa-solid fa-calendar text-surface-500 text-lg hover:!text-surface-800 cursor-pointer",
                    onClick: jumpToToday,
                    onKeyup: _withKeys(jumpToToday, ["enter"]),
                    tabindex: "0"
                }, null, 32),
                _createElementVNode("div", _hoisted_2, [
                    _createVNode(_component_DatePickerControlButton, {
                        "selected-interval": __props.selectedInterval,
                        "icon-direction": "left",
                        onClick: onPrevious
                    }, null, 8, ["selected-interval"]),
                    _createElementVNode("div", {
                        class: "flex items-center justify-center gap-2 text-xs text-surface-600 border border-surface-200 rounded-lg px-3 py-1 cursor-pointer",
                        onClick: _cache[0] || (_cache[0] = ($event) => (openDatePickerPopover($event))),
                        onKeyup: _cache[1] || (_cache[1] = _withKeys(($event) => (openDatePickerPopover($event)), ["enter"])),
                        tabindex: "0"
                    }, [
                        _createElementVNode("span", _hoisted_3, _toDisplayString(displayLabel.value), 1)
                    ], 32),
                    _createVNode(_component_DatePickerControlButton, {
                        "selected-interval": __props.selectedInterval,
                        "icon-direction": "right",
                        onClick: onNext
                    }, null, 8, ["selected-interval"])
                ]),
                _createVNode(DatePickerPopover, {
                    ref_key: "datePickerRef",
                    ref: datePickerRef,
                    modelValue: datePickerValue.value,
                    "onUpdate:modelValue": updateDatePickerValue,
                    customLabel: datePickerCustomLabel.value,
                    "onUpdate:customLabel": updateDatePickerCustomLabel,
                    "selected-interval": __props.selectedInterval
                }, null, 8, ["modelValue", "customLabel", "selected-interval"])
            ]));
        };
    }
});
